import {
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  MeetingFieldsFragment,
  useListTeamsQuery,
  useListUsersQuery,
} from '@/@generated/codegen/API';
import { AppSpinner } from '@/ui/AppSpinner';
import ItemList from '@/ui/ItemList/ItemList';
import { DEFAULT_HOST_VALUE } from '@/utils/constants/app.constants';
import { useFormFieldArrayHelpers } from '@/utils/hooks/useFormFieldArrayHelpers';

import { CreateMeetingFormData } from '../createMeetingFormSchema';

type Props = {
  form: UseFormReturn<CreateMeetingFormData>;
  meeting?: MeetingFieldsFragment;
  isDuplicateMeeting?: boolean;
};

export const CreateMeetingFormColumnTwo = ({ form, meeting, isDuplicateMeeting }: Props) => {
  const { t } = useTranslation();
  const teams = useListTeamsQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where: {},
    },
  });
  const teamList = useMemo(() => {
    return teams.data?.listTeams.edges.map((edge) => edge.node);
  }, [teams.data]);
  const users = useListUsersQuery({
    variables: {
      orderByArgs: {
        direction: 'asc',
        orderByField: 'email',
      },
      paginationArgs: {
        first: 100,
      },
      where: {
        isGuest: {
          equals: false,
        },
        isMocked: {
          equals: false,
        },
      },
    },
  });

  const {
    formState: { errors },
  } = form;
  const {
    handleAdd: handleAddTeam,
    handleRemove: handleRemoveTeam,
    getAvailableItems: getAvailableTeams,
  } = useFormFieldArrayHelpers({
    form,
    arrayFieldName: 'meetingTeamIDs',
    selectorFieldName: 'selectedTeamID',
    allElements: teamList ?? [],
  });

  const currentMeetingTeamIDs = form.watch('meetingTeamIDs') ?? [];

  const onTeamChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      form.setValue('selectedTeamID', e.target.value);
      handleAddTeam();
      form.setValue('selectedTeamID', '');
    },
    [form, handleAddTeam]
  );

  const getTeamNameById = useCallback(
    (id: string) => {
      if (!teams.data) return null;
      const team = teams.data.listTeams.edges.find((edge) => edge.node.id === id);
      return team ? team.node.name : null;
    },
    [teams.data]
  );

  if (!teams.data || !users.data) {
    return (
      <Flex w="full" h="full" justifyContent="center" alignItems="center">
        <AppSpinner />
      </Flex>
    );
  }

  return (
    <>
      <FormControl isRequired>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.meetingTeams.label')}
        </FormLabel>
        <Controller
          name="selectedTeamID"
          control={form.control}
          render={({ field }) => (
            <Select
              placeholder={
                t('admin.createMeeting.form.meetingTeams.placeholder') || 'Click to add a team'
              }
              {...field}
              onChange={onTeamChange}
              value={field.value ?? undefined}
              required={form.getValues('meetingTeamIDs').length === 0}
              aria-label="Add a team to the meeting"
            >
              {getAvailableTeams().map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </Select>
          )}
        />
        <VStack p={2} align="flex-start">
          {currentMeetingTeamIDs.length && (
            <ItemList
              itemList={currentMeetingTeamIDs.map((teamId) => ({
                id: teamId,
                value: getTeamNameById(teamId)!,
              }))}
              removeItemFunc={handleRemoveTeam}
            />
          )}
          {currentMeetingTeamIDs.length === 0 && (
            <Text>{t('admin.createMeeting.form.meetingTeams.empty')}</Text>
          )}
        </VStack>
      </FormControl>

      <FormControl isRequired isInvalid={Boolean(errors.hostUserID)}>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.host.label')}
        </FormLabel>
        <Select
          aria-label="Host of the meeting"
          data-test="meeting-host-dropdDown"
          {...form.register('hostUserID', {
            required: true,
            validate: (value) => {
              if (value === DEFAULT_HOST_VALUE) return false;
              return true;
            },
          })}
        >
          <option key={DEFAULT_HOST_VALUE} value={DEFAULT_HOST_VALUE}>
            {t('admin.createMeeting.form.host.placeholder')}
          </option>
          {users.data &&
            users.data.listUsers.edges
              .map((el) => el.node)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.email}
                </option>
              ))}
        </Select>

        {errors.hostUserID && (
          <FormHelperText color="red">
            {t('admin.createMeeting.form.meetingHost.error')}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isRequired isInvalid={Boolean(errors.hostFullName)}>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.hostFullName.label')}
        </FormLabel>
        <Input
          aria-label="Enter host full name"
          type="text"
          {...form.register('hostFullName', { required: true })}
          placeholder="John Doe"
        />
        {errors.hostFullName && (
          <FormHelperText color="red">
            {t('admin.createMeeting.form.hostFullName.error')}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isRequired isInvalid={Boolean(errors.purchaseOrderNbr)}>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.purchaseOrderNbr.label')}
        </FormLabel>
        <Input
          type="text"
          {...form.register('purchaseOrderNbr', { required: true })}
          aria-label="Enter purchase order number"
        />
        {errors.purchaseOrderNbr && (
          <FormHelperText color="red">
            {t('admin.createMeeting.form.purchaseOrderNbr.error')}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isInvalid={Boolean(errors.customNotes)}>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.customNotes.label')}
        </FormLabel>
        <Input type="text" {...form.register('customNotes')} aria-label="Enter Custom note" />
        {errors.customNotes && (
          <FormHelperText color="red">
            {t('admin.createMeeting.form.customNotes.error')}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isInvalid={Boolean(errors.streamUrl)}>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.streamUrl.label')}
        </FormLabel>
        <Input type="text" {...form.register('streamUrl')} aria-label="Enter a Stream URL" />
        {errors.streamUrl && (
          <FormHelperText color="red">
            {t('admin.createMeeting.form.streamUrl.error')}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.demo.label')}
        </FormLabel>
        <Switch
          {...form.register('isDemo', { required: false })}
          disabled={!!meeting && !isDuplicateMeeting}
          defaultChecked={meeting?.isDemo || false}
          style={{
            alignItems: 'center',
            display: 'flex',
          }}
          aria-label="Is it a demo meeting?"
        >
          <Text opacity={!!meeting && !isDuplicateMeeting ? 0.6 : 1}>
            {t('admin.createMeeting.form.demo.text')}
          </Text>
        </Switch>
      </FormControl>

      {/* <FormControl>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMeeting.form.settings.label')}
        </FormLabel>
        <Checkbox
          {...form.register('generateGuestUrl', { required: false })}
          aria-label="Generate a guest URL?"
        >
          {t('admin.createMeeting.form.guestUrl.text')}
        </Checkbox>
      </FormControl> */}
    </>
  );
};
